import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, MapPin, Search, Star } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useApi } from "../../lib/useApi";
import { Loader, ErrorState, EmptyState } from "../common/States";
import { ORG_TYPE } from "../../lib/labels";

const EMPTY = { q: "", sector: "", city: "", country: "", service: "" };

const CompanyCard = ({ c, to }) => (
  <Link to={to} data-testid={`company-card-${c.slug}`} className="card-surface group block p-5 transition-colors hover:border-blue-300">
    <div className="flex items-start justify-between gap-3">
      <h3 className="font-display font-semibold group-hover:text-blue-700">{c.name}</h3>
      <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{ORG_TYPE[c.org_type]}</span>
    </div>
    <p className="mt-1 text-sm text-slate-500">{c.sector}</p>
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{c.city}, {c.country}</span>
      <span>{c.agency_count} agence(s)</span>
      {c.review_count > 0 ? <span className="flex items-center gap-1 font-medium text-slate-700"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />{c.avg_rating} · {c.review_count} avis</span> : <span>Aucun avis publié</span>}
    </div>
  </Link>
);

export default function CompanySearch({ linkFor }) {
  const [form, setForm] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const options = useApi("/public/filters");
  const { data, loading, error, reload } = useApi(`/public/companies?${new URLSearchParams(applied).toString()}`);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const opts = options.data || { countries: [], sectors: [], cities: [] };

  return (
    <div>
      <form onSubmit={(e) => { e.preventDefault(); setApplied(form); }} className="card-surface grid gap-2 p-3 sm:grid-cols-2 lg:grid-cols-6" data-testid="search-form">
        <div className="relative lg:col-span-2">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input value={form.q} onChange={set("q")} placeholder="Nom de l'organisation" className="h-10 pl-9" data-testid="search-input" />
        </div>
        <Input list="dl-sectors" value={form.sector} onChange={set("sector")} placeholder="Secteur" className="h-10" data-testid="search-sector-input" />
        <Input list="dl-cities" value={form.city} onChange={set("city")} placeholder="Ville" className="h-10" data-testid="search-city-input" />
        <select className="field-select" value={form.country} onChange={set("country")} data-testid="search-country-select">
          <option value="">Tous les pays</option>
          {opts.countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <Input value={form.service} onChange={set("service")} placeholder="Service (ex : accueil)" className="h-10" data-testid="search-service-input" />
        <datalist id="dl-sectors">{opts.sectors.map((s) => <option key={s} value={s} />)}</datalist>
        <datalist id="dl-cities">{opts.cities.map((s) => <option key={s} value={s} />)}</datalist>
        <div className="flex gap-2 sm:col-span-2 lg:col-span-6 lg:justify-end">
          <Button type="button" variant="ghost" onClick={() => { setForm(EMPTY); setApplied(EMPTY); }} data-testid="search-reset">Réinitialiser</Button>
          <Button type="submit" className="flex-1 lg:flex-none" data-testid="search-submit">Rechercher</Button>
        </div>
      </form>
      <div className="mt-6">
        {loading && <Loader />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {data && !data.length && <EmptyState icon={Building2} title="Aucune organisation trouvée" text="Modifiez ou réinitialisez vos critères de recherche." />}
        {data && data.length > 0 && <p className="mb-3 text-sm text-slate-500" data-testid="search-count">{data.length} organisation(s)</p>}
        <div className="grid gap-3 sm:grid-cols-2">{data?.map((c) => <CompanyCard key={c.id} c={c} to={linkFor(c)} />)}</div>
      </div>
    </div>
  );
}
